import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play } from 'lucide-react';
import { helpContent, DEFAULT_HELP } from './help-content';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  route: string;
  drawerOpen: boolean;
  chatOpen: boolean;
  isMobile: boolean;
}
export function HelpSheet({
  isOpen,
  onClose,
  route,
  drawerOpen,
  chatOpen,
  isMobile
}: Props) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  const entry = helpContent[route] ?? DEFAULT_HELP;
  if (isMobile) {
    return <AnimatePresence data-id="element-1068">
        {isOpen && <motion.div key="help-sheet-mobile-backdrop" initial={{
        opacity: 0,
        pointerEvents: 'auto' as const
      }} animate={{
        opacity: 1,
        pointerEvents: 'auto' as const
      }} exit={{
        opacity: 0,
        pointerEvents: 'none' as const
      }} transition={{
        duration: 0.2
      }} className="fixed inset-x-0 bottom-0 top-14 z-50 bg-black/60 flex items-end" onClick={onClose} data-id="element-1069">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 34
        }} className="bg-[#1a1a20] w-full h-[calc(100dvh-56px-48px)] rounded-t-[20px] border-t border-x border-white/[0.06] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1070">
              <HeaderBar title={entry.title} onClose={onClose} data-id="element-1071" />
              <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6" data-id="element-1072">
                <MockVideo caption={entry.videoCaption} data-id="element-1073" />
                <pre className="whitespace-pre-wrap font-sans text-[14px] text-[#c4bfb8] leading-relaxed" data-id="element-1074">
                  {entry.body}
                </pre>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>;
  }

  // Desktop: bottom dock, 45vh, constrained to the main content column
  return <AnimatePresence data-id="element-1075">
      {isOpen && <motion.aside key="help-sheet-desktop" initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} style={{
      left: drawerOpen ? 280 : 0,
      right: chatOpen ? 400 : 0
    }} className="fixed bottom-0 h-[45vh] bg-[#1a1a20] border-t border-white/[0.06] z-20 flex flex-col" data-id="element-1076">
          <HeaderBar title={entry.title} onClose={onClose} data-id="element-1077" />
          <div className="flex-1 overflow-y-auto p-6 grid grid-cols-[60%_40%] gap-6" data-id="element-1078">
            <pre className="whitespace-pre-wrap font-sans text-[14px] text-[#c4bfb8] leading-relaxed" data-id="element-1079">
              {entry.body}
            </pre>
            <MockVideo caption={entry.videoCaption} data-id="element-1080" />
          </div>
        </motion.aside>}
    </AnimatePresence>;
}
function HeaderBar({
  title,
  onClose
}: {
  title: string;
  onClose: () => void;
}) {
  return <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]" data-id="element-1081">
      <h2 className="text-[18px] font-medium text-[#e8e4df]" data-id="element-1082">{title}</h2>
      <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors" aria-label="Close help" data-id="element-1083">
        <X size={18} className="text-[#8a8680]" data-id="element-1084" />
      </button>
    </div>;
}
function MockVideo({
  caption
}: {
  caption: string;
}) {
  return <div data-id="element-1085">
      <div className="aspect-video bg-[#0d1117] rounded-[12px] flex items-center justify-center border border-white/[0.06]" data-id="element-1086">
        <div className="w-14 h-14 rounded-full bg-[#4a9a9a]/15 flex items-center justify-center" data-id="element-1087">
          <Play size={22} className="text-[#4a9a9a] ml-0.5" data-id="element-1088" />
        </div>
      </div>
      <p className="text-[12px] text-[#8a8680] mt-2" data-id="element-1089">{caption}</p>
    </div>;
}